import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep, maxInputNumber } from "@/constants/inputs";
import useCUCompositeJobStore from "@/store/useCUCompositeJobStore";
import { getFloat } from "@/utils/mappings";

import ExpandableSection from "../../../../components/job/ExpandableSection";

const cardName = "externalPressureVacuumSection";
const externalPressureVacuumSectionSchema = z.object({
  external_pressure_vacuum: z.union([
    z.number().lt(maxInputNumber).gt(0),
    z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
  ]),
});
type ExternalPressureVacuumSectionSchema = z.infer<typeof externalPressureVacuumSectionSchema>;
export function ExternalPressureVacuum() {
  const { sections, toggleExpandableSection, setExternalPressureVacuumSection } = useCUCompositeJobStore();
  const savedData = sections.externalPressureVacuumSection?.data;

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<ExternalPressureVacuumSectionSchema>({
    resolver: zodResolver(externalPressureVacuumSectionSchema),
    values: {
      external_pressure_vacuum: savedData?.external_pressure_vacuum ?? "",
    },
  });

  return (
    <ExpandableSection
      status={sections.externalPressureVacuumSection?.status ?? "hidden"}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="External Pressure Vacuum"
    >
      <div>
        <form
          className="flex flex-col items-center gap-6 md:flex-row md:gap-20"
          onSubmit={handleSubmit((data) => {
            if (!data.external_pressure_vacuum) return;
            const external_pressure_vacuum =
              typeof data.external_pressure_vacuum === "string"
                ? Number.parseFloat(data.external_pressure_vacuum)
                : data.external_pressure_vacuum;
            setExternalPressureVacuumSection({ data: { external_pressure_vacuum }, status: "done" });
          })}
        >
          <TextInput
            className="flex-1 basis-full md:basis-60"
            label="External pressure vacuum"
            type="number"
            step={inputStep}
            error={errors.external_pressure_vacuum?.message}
            {...register("external_pressure_vacuum", { setValueAs: getFloat })}
            endAdornment={
              <Typography variant="text-small" weight="regular" className="text-gray-400">
                PSI
              </Typography>
            }
          />
          <Button className="w-full self-end md:w-fit" type="submit">
            Save
          </Button>
        </form>
      </div>
    </ExpandableSection>
  );
}
